import { useState, useEffect, useCallback } from "react";
import {
  InputAdornment,
  Paper,
  TextField,
  TablePagination,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import DataTable from "components/utils/DataTable";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as DeleteIcon } from "images/svg/delete-02.svg";
import BackIcon from "images/svg/back-icon.svg";
import { ROUTES, COLLECTIONS, DRAFT_STATUS } from "const";
import Info from "components/utils/Alert/Info";
import toast from "react-hot-toast";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import { db } from "helpers/DBHelper";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import StockDamageHelper from "helpers/StockDamageHelper";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";

function DraftStockDamages() {
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [selectedDrafts, setSelectedDrafts] = useState([]);

  const navigate = useNavigate();

  const fetchDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const allDamages = await db.getAll(COLLECTIONS.STOCK_DAMAGES.name);
      const draftDamages = allDamages.filter((d) => d.status === DRAFT_STATUS.DRAFT || (!d.status && !d.id));

      const sortedData = draftDamages.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDrafts(sortedData);
    } catch (error) {
      console.error("Failed to fetch drafts", error);
      toast.error("Failed to load drafts from local database");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
  };

  const handleConfirmDelete = async (row) => {
    if (!row) return;
    try {
      await db.delete(COLLECTIONS.STOCK_DAMAGES.name, row.local_id);
      toast.success("Draft deleted");
      fetchDrafts();
    } catch (error) {
      console.error("Failed to delete draft", error);
      toast.error("Failed to delete draft");
    }
  };

  const handleSearch = (value) => {
    setFilterText(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDrafts = drafts.filter((d) => {
    const searchStr = filterText.toLowerCase();
    const dateStr = (DateTimeHelpers.getDate(d.date) || "").toLowerCase();
    return (
      dateStr.includes(searchStr) ||
      (d.name || "").toLowerCase().includes(searchStr) ||
      (d.productId || "").toString().includes(searchStr)
    );
  });

  const paginatedDrafts = filteredDrafts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDrafts(filteredDrafts.map((d) => d.local_id));
    } else {
      setSelectedDrafts([]);
    }
  };

  const handleSelectRow = (local_id) => {
    setSelectedDrafts((prev) => (prev.includes(local_id) ? prev.filter((id) => id !== local_id) : [...prev, local_id]));
  };

  const headerData = [
    {
      label: (
        <Checkbox
          checked={filteredDrafts.length > 0 && selectedDrafts.length === filteredDrafts.length}
          indeterminate={selectedDrafts.length > 0 && selectedDrafts.length < filteredDrafts.length}
          onChange={handleSelectAll}
          size="small"
        />
      ),
      id: "select",
      type: "component",
      viewRender: (row) => (
        <Checkbox
          checked={selectedDrafts.includes(row.local_id)}
          onChange={() => handleSelectRow(row.local_id)}
          size="small"
        />
      ),
      width: "50px",
    },
    {
      label: "Date",
      id: "date",
      type: "callback",
      viewRender: (row) => DateTimeHelpers.getDate(row.date) || "-",
    },
    {
      label: "Product ID",
      id: "productId",
      type: "text",
    },
    {
      label: "Item Name",
      id: "name",
      type: "text",
    },
    {
      label: "Cost",
      id: "price",
      type: "floatAmount",
    },
    {
      label: "Quantity",
      id: "qty",
      type: "callback",
      viewRender: (row) => `${row.qty} ${row.unit || "Unit"}`,
    },
    {
      label: "Total",
      id: "payable",
      type: "floatAmount",
    },
    {
      label: "Actions",
      id: "action",
      type: "callback",
      viewRender: (row) => (
        <IconButton size="small" onClick={() => handleDeleteClick(row)} style={{ color: "#7c7c7d" }}>
          <DeleteIcon className={styles.deleteIcon} />
        </IconButton>
      ),
    },
  ];

  const handleSaveDamage = useCallback(async () => {
    if (selectedDrafts.length === 0) {
      toast.error("Please select at least one draft to save");
      return;
    }

    const draftsToSave = drafts.filter((d) => selectedDrafts.includes(d.local_id));

    setIsSaving(true);
    try {
      await StockDamageHelper.saveStockDamageToCloudAndLocal(draftsToSave);
      toast.success("Draft damages recorded successfully");
      setSelectedDrafts([]);
      fetchDrafts();
    } catch (error) {
      console.error("Failed to save damage", error);
      toast.error(error.message || "Failed to save stock damage");
    } finally {
      setIsSaving(false);
    }
  }, [drafts, selectedDrafts, fetchDrafts]);

  return (
    <div style={{ padding: "10px 10px 0 10px", height: "100%", boxSizing: "border-box" }}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.leftHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(ROUTES.STOCK_DAMAGES)}
              aria-label="Back to Stock Damages"
            >
              <img src={BackIcon} alt="Back to Stock Damages" />
            </button>
            <div className={styles.header}>
              <h2>Draft Stock Damages</h2>
              <h5>Manage and Track Unsaved Stock Damages</h5>
            </div>
          </div>

          <div className={styles.contentWrapper}>
            <TextField
              placeholder="Search"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ButtonRed onClick={handleSaveDamage} disabled={isSaving} style={{ minWidth: "120px", marginLeft: "12px" }}>
              {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save Damage"}
            </ButtonRed>
          </div>
        </div>

        <div className={styles.tableContainer}>
          {isLoading ? (
            <SkeletonLoading isOpen={true} count={30} height={40} xs={12} sm={6} md={4} lg={4} />
          ) : filteredDrafts && filteredDrafts.length > 0 ? (
            <Paper
              elevation={0}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div style={{ flex: 1, overflowY: "auto" }}>
                <DataTable columns={headerData} rows={paginatedDrafts} />
              </div>

              <div className={styles.pagination}>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={filteredDrafts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </Paper>
          ) : (
            <Info title="No drafts to list" content="You have no draft damages." />
          )}
        </div>
      </div>

      <ConfirmPopup
        data={rowToDelete}
        handleClose={() => setRowToDelete(null)}
        handleOkay={handleConfirmDelete}
        alertTitle="Delete Draft"
        alertContent="Are you sure you want to delete this draft?"
        successBtnName="Delete"
      />
    </div>
  );
}

export default DraftStockDamages;
